import React, {useEffect} from 'react';
import ViewportSliderBar from 'Components/ViewportSliderBar';
import {useViewStore} from 'stores/view';
import {AstView, EnrichedAstNode} from 'rendition-core/types';
import {useAddKeyFramesToDocument} from 'Hooks/useAddKeyFramesToDocument';

interface Props {
    background: string;
    previewAreaSize: number;
    styles?: React.CSSProperties;
    resizable?: boolean;
    view: AstView;
    rootNode?: EnrichedAstNode;
    overLayedChildren?: React.ReactNode;
    rendersSelectedNodeAsRoot?: boolean;
    onNodeSelection?: (nodeIds: string[]) => void;
}

export const ScalablePreviewFixed: React.FC<React.PropsWithChildren<Props>> = ({
    children,
    overLayedChildren,
    resizable = true,
    view,
    previewAreaSize,
    background,
    styles,
    rootNode = view.tree,
    rendersSelectedNodeAsRoot = false,
    onNodeSelection,
}) => {
    const startingViewportSizeInSlider: number = view.tree.position.width;
    //view.tree.position.width / view.tree.position.height > 0.6 ? Math.min(view.tree.position.width, 2000) : 1366;

    const previewSize = useViewStore.use.previewWidth();
    const setPreviewSize = useViewStore.use.setPreviewWidth();

    useEffect(() => {
        setPreviewSize(startingViewportSizeInSlider);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const rootAdjustedPreviewSize = rendersSelectedNodeAsRoot
            ? previewSize * (view.tree.position.width / rootNode.position.width)
            : previewSize;

        document.documentElement.style.setProperty('--preview-width', `${rootAdjustedPreviewSize}px`);
    }, [previewSize, rendersSelectedNodeAsRoot, rootNode.position.width, view.tree.position.width]);

    useAddKeyFramesToDocument(view);

    const scaleFactor = previewSize < previewAreaSize ? 1 : previewAreaSize / previewSize;

    return (
        <div
            style={{
                width: previewAreaSize,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
            }}
        >
            {resizable && (
                <ViewportSliderBar previewSize={previewSize} setPreviewSize={setPreviewSize}></ViewportSliderBar>
            )}
            <div
                style={{
                    position: 'relative',
                    width: Math.min(previewSize, previewAreaSize),
                    zIndex: 100,
                }}
            >
                {overLayedChildren}
            </div>

            <div
                style={{
                    width: previewAreaSize,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                }}
            >
                <div
                    style={{
                        width: previewSize,
                        overflow: 'hidden',
                        transform: `scale(${scaleFactor})`,
                        transformOrigin: 'top center',
                        position: 'relative',
                        backgroundColor: background,
                        ...styles,
                    }}
                >
                    {children}
                </div>
            </div>
        </div>
    );
};
